import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useI18n } from '../../contexts/I18nContext';

interface FilePreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    fileUrl: string;
    fileName: string;
    fileType?: string;
}

export default function FilePreviewModal({
    isOpen,
    onClose,
    fileUrl,
    fileName,
    fileType = ''
}: FilePreviewModalProps) {
    const { language } = useI18n();

    const getFileExtension = (filename: string): string => {
        return filename.split('.').pop()?.toLowerCase() || '';
    };

    const isImage = (filename: string, mimeType: string): boolean => {
        const ext = getFileExtension(filename);
        return (
            ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext) ||
            mimeType.startsWith('image/')
        );
    };

    const isPDF = (filename: string, mimeType: string): boolean => {
        const ext = getFileExtension(filename);
        return ext === 'pdf' || mimeType === 'application/pdf';
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderPreview = () => {
        if (isImage(fileName, fileType)) {
            return (
                <div className="relative bg-gray-900 rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden flex items-center justify-center min-h-[150px] xs:min-h-[200px] sm:min-h-[250px] md:min-h-[350px] h-[40vh] xs:h-[45vh] sm:h-[50vh] md:h-[55vh] lg:h-[60vh]">
                    <img
                        src={fileUrl}
                        alt={fileName}
                        className="w-full h-full object-contain p-1 sm:p-2"
                    />
                </div>
            );
        }

        if (isPDF(fileName, fileType)) {
            return (
                <div className="relative rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden h-[40vh] xs:h-[45vh] sm:h-[50vh] md:h-[55vh] lg:h-[60vh] bg-gray-100">
                    <iframe
                        src={fileUrl}
                        className="w-full h-full border-0"
                        title={fileName}
                    />
                </div>
            );
        }

        // Unsupported file type
        return (
            <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-center">
                <svg
                    className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto mb-3 sm:mb-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                </svg>
                <p className="text-[#707070] font-medium mb-3 sm:mb-4 text-sm sm:text-base">
                    {language === 'ar'
                        ? 'لا يمكن عرض هذا النوع من الملفات'
                        : 'Preview not available for this file type'}
                </p>
                <button
                    onClick={handleDownload}
                    className="px-4 sm:px-6 py-2 sm:py-2.5 bg-[#5B3FD0] text-white rounded-xl font-bold hover:bg-[#4A2FB0] transition-colors text-sm sm:text-base"
                >
                    {language === 'ar' ? 'تحميل الملف' : 'Download File'}
                </button>
            </div>
        );
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-[calc(100vw-16px)] sm:w-[calc(100vw-32px)] md:w-[90vw] lg:w-[85vw] xl:max-w-5xl max-h-[95vh] sm:max-h-[90vh] transform overflow-hidden rounded-xl sm:rounded-2xl md:rounded-3xl bg-white shadow-2xl transition-all flex flex-col">
                                {/* Header */}
                                <div className="flex items-center justify-between p-2 sm:p-3 md:p-4 lg:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
                                    <div className="flex-1 min-w-0 mr-2 sm:mr-3">
                                        <Dialog.Title
                                            as="h3"
                                            className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-[#2C2C2C] truncate max-w-[150px] xs:max-w-[200px] sm:max-w-[300px] md:max-w-[400px] lg:max-w-none"
                                        >
                                            {fileName}
                                        </Dialog.Title>
                                    </div>

                                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                        {/* Download Button */}
                                        <button
                                            onClick={handleDownload}
                                            className="p-2 sm:p-2.5 text-[#5B3FD0] hover:bg-[#5B3FD0]/10 rounded-lg sm:rounded-xl transition-colors"
                                            title={language === 'ar' ? 'تحميل' : 'Download'}
                                        >
                                            <svg
                                                className="w-4 h-4 sm:w-5 sm:h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                                />
                                            </svg>
                                        </button>

                                        {/* Close Button */}
                                        <button
                                            onClick={onClose}
                                            className="p-2 sm:p-2.5 text-gray-500 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-colors"
                                            title={language === 'ar' ? 'إغلاق' : 'Close'}
                                        >
                                            <svg
                                                className="w-4 h-4 sm:w-5 sm:h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Preview Content */}
                                <div className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 lg:p-6">{renderPreview()}</div>

                                {/* Footer */}
                                <div className="flex flex-row items-center justify-end gap-2 sm:gap-3 p-2 sm:p-3 md:p-4 lg:p-5 border-t border-gray-100 bg-gray-50 flex-shrink-0">
                                    <button
                                        onClick={onClose}
                                        className="flex-1 xs:flex-none px-3 sm:px-5 py-2 sm:py-2.5 border border-gray-300 text-[#2C2C2C] rounded-lg sm:rounded-xl font-bold hover:bg-gray-100 transition-colors text-xs sm:text-sm"
                                    >
                                        {language === 'ar' ? 'إغلاق' : 'Close'}
                                    </button>
                                    <button
                                        onClick={handleDownload}
                                        className="flex-1 xs:flex-none px-3 sm:px-5 py-2 sm:py-2.5 bg-[#5B3FD0] text-white rounded-lg sm:rounded-xl font-bold hover:bg-[#4A2FB0] transition-colors flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                                    >
                                        <svg
                                            className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                            />
                                        </svg>
                                        {language === 'ar' ? 'تحميل' : 'Download'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
